import React, { useState } from 'react'
import { PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { useMyCommissions as useCommissions } from '@/hooks/useCommission'
import { useMyWithdrawals as useWithdrawals, useRequestWithdrawal } from '@/hooks/useWithdrawal'

export const AffiliateWithdrawPage: React.FC = () => {
    const { user } = useAuth()
    const { data: commissions } = useCommissions(user?.id || '')
    const { data: withdrawals } = useWithdrawals(user?.id || '')
    const requestWithdrawal = useRequestWithdrawal()
    const [amount, setAmount] = useState('')
    const [bankName, setBankName] = useState('')
    const [accountNumber, setAccountNumber] = useState('')
    const [accountName, setAccountName] = useState('')
    const [submitError, setSubmitError] = useState<string | null>(null)

    const availableBalance = commissions?.filter((c: any) => c.status === 'earned')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0
    const minWithdrawal = 100000 // From system config

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-500/20 text-yellow-400'
            case 'approved': return 'bg-blue-500/20 text-blue-400'
            case 'completed': return 'bg-green-500/20 text-green-400'
            case 'rejected': return 'bg-red-500/20 text-red-400'
            default: return 'bg-gray-500/20 text-gray-500 dark:text-gray-400'
        }
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        setSubmitError(null)

        if (!user?.id) {
            setSubmitError('User not authenticated')
            return
        }

        const amountNum = parseInt(amount, 10)
        if (isNaN(amountNum) || amountNum < minWithdrawal) {
            setSubmitError(`Minimum withdrawal amount is Rp ${minWithdrawal.toLocaleString('id-ID')}`)
            return
        }

        requestWithdrawal.mutate({
            user_id: user.id,
            amount: amountNum,
            bank_name: bankName,
            account_number: accountNumber,
            account_name: accountName,
        }, {
            onSuccess: () => {
                setAmount('')
                setBankName('')
                setAccountNumber('')
                setAccountName('')
            },
            onError: (error: any) => {
                setSubmitError(error?.response?.data?.error?.message || 'Failed to submit withdrawal request')
            }
        })
    }

    return (
        <div>
            <PageHeader
                title="Request Withdrawal"
                subtitle="Withdraw your available balance"
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Withdrawal Form */}
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-6">New Withdrawal Request</h3>

                    {/* Balance Info */}
                    <div className="mb-6 p-4 rounded-lg bg-primary/10 border border-primary/20">
                        <p className="text-xs text-gray-500 dark:text-text-muted mb-1">Available Balance</p>
                        <p className="text-2xl font-bold text-red-400">Rp {availableBalance.toLocaleString('id-ID')}</p>
                        <p className="text-xs text-gray-500 dark:text-text-muted mt-2">Minimum withdrawal: Rp {minWithdrawal.toLocaleString('id-ID')}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-2">Amount (IDR)</label>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="Enter amount"
                                min={minWithdrawal}
                                max={availableBalance}
                                className="w-full px-4 py-2.5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-2">Bank Name</label>
                            <select
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                                className="w-full px-4 py-2.5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
                                required
                            >
                                <option value="" className="bg-gray-50 dark:bg-surface-dark">Select bank</option>
                                <option value="BCA" className="bg-gray-50 dark:bg-surface-dark">BCA</option>
                                <option value="Mandiri" className="bg-gray-50 dark:bg-surface-dark">Mandiri</option>
                                <option value="BNI" className="bg-gray-50 dark:bg-surface-dark">BNI</option>
                                <option value="BRI" className="bg-gray-50 dark:bg-surface-dark">BRI</option>
                                <option value="CIMB" className="bg-gray-50 dark:bg-surface-dark">CIMB Niaga</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-2">Account Number</label>
                            <input
                                type="text"
                                value={accountNumber}
                                onChange={(e) => setAccountNumber(e.target.value)}
                                placeholder="Enter account number"
                                className="w-full px-4 py-2.5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-2">Account Holder Name</label>
                            <input
                                type="text"
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                                placeholder="Name as shown on bank account"
                                className="w-full px-4 py-2.5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={availableBalance < minWithdrawal || requestWithdrawal.isPending}
                            className="w-full py-3 bg-primary text-white rounded-lg text-sm font-bold hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                        >
                            {requestWithdrawal.isPending ? 'Submitting...' : 'Submit Withdrawal Request'}
                        </button>
                        {availableBalance < minWithdrawal && (
                            <p className="text-xs text-red-400 text-center">
                                Balance below minimum withdrawal amount
                            </p>
                        )}
                        {submitError && (
                            <p className="text-xs text-red-400 text-center">
                                {submitError}
                            </p>
                        )}
                        {requestWithdrawal.isSuccess && (
                            <p className="text-xs text-green-400 text-center">
                                Withdrawal request submitted successfully!
                            </p>
                        )}
                    </form>
                </div>

                {/* Withdrawal History */}
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-200 dark:border-white/5">
                        <h3 className="font-bold text-gray-900 dark:text-white">Withdrawal History</h3>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-white/5">
                        {withdrawals?.length === 0 ? (
                            <div className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">No withdrawal requests yet</div>
                        ) : (
                            // Show only the 10 most recent withdrawals for history preview
                            withdrawals?.slice(0, 10).map((w: any) => (
                                <div key={w.id} className="px-5 py-4 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-bold text-gray-900 dark:text-white">Rp {(w.amount || 0).toLocaleString('id-ID')}</p>
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${getStatusBadgeColor(w.status)}`}>
                                            {w.status}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-text-muted">
                                        <span>
                                            {w.bankName && w.accountNumber
                                                ? `${w.bankName} - ${w.accountNumber}`
                                                : 'Bank info not available'}
                                        </span>
                                        <span>{w.createdAt ? new Date(w.createdAt).toLocaleDateString('id-ID') : '-'}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AffiliateWithdrawPage
